import useSorting from '@rio-cloud/rio-uikit/useSorting';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/utils/SortUtils';
import type React from 'react';
import { useMemo } from 'react';

import type { Column } from '@/components/Table/Table';

function normalize(v: unknown) {
    if (typeof v === 'boolean') {
        return v ? 0 : 1;
    }

    if (v instanceof Date) {
        return v.getTime();
    }

    if (typeof v === 'string') {
        if (/^\d+(\.\d+)?$/.test(v)) {
            return Number(v);
        }

        if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(v)) {
            const ts = Date.parse(v);
            if (!Number.isNaN(ts)) {
                return ts;
            }
        }

        return v.toLowerCase();
    }

    return v;
}

function compare(a: unknown, b: unknown) {
    const na = normalize(a);
    const nb = normalize(b);

    if (typeof na === 'number' && typeof nb === 'number') {
        return na - nb;
    }

    return String(na).localeCompare(String(nb), undefined, { numeric: true, sensitivity: 'base' });
}

export function useTableSorting<T>(
    data: T[],
    columns: Column<T>[],
    initialKey: keyof T,
    initialSortDirection: SortDirectionType
) {
    const sorting = useSorting(data, initialKey, initialSortDirection);
    const { sortKey, setSortKey, sortDirection, setSortDirection, toggleDirection } = sorting;

    const sortedData = useMemo(() => {
        if (!sortKey || !sortDirection) {
            return data;
        }

        const col = columns.find(c => c.key === sortKey);
        if (!col) {
            return data;
        }

        const getVal = (row: T) =>
            col.valueGetter ? col.valueGetter(row) : col.key ? (row as Record<string, unknown>)[col.key] : undefined;

        const copy = [...data];
        copy.sort((a, b) => {
            const cmp = compare(getVal(a), getVal(b));
            return sortDirection === SortDirection.ASCENDING ? cmp : -cmp;
        });
        return copy;
    }, [data, columns, sortKey, sortDirection]);

    const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
        const newSortBy = event.currentTarget.getAttribute('data-sortby') as keyof T | null;
        if (!newSortBy) {
            return;
        }

        if (newSortBy === sortKey) {
            toggleDirection();
        } else {
            setSortKey(newSortBy);
            setSortDirection(SortDirection.ASCENDING);
        }
    };

    return { ...sorting, sortedData, handleSortChange };
}
